/**
 * @file components/common/responsive-columns.ts
 * @description antd Table column 的響應式工具，配合 antd 內建的 `responsive` 屬性使用。
 *
 * antd Table column 有一個 `responsive: Breakpoint[]` 屬性 — 只在指定斷點下顯示該列。
 * 例如 `responsive: ['lg', 'xl', 'xxl']` 表示 < lg（即 < 992px，iPad 直立）時隱藏這列。
 *
 * 使用範例：
 * ```tsx
 * import { hideBelow } from '@/components/common/responsive-columns';
 *
 * const columns: ColumnsType<Item> = [
 *   { title: '案件編號', dataIndex: 'caseCode', width: 120 },
 *   { title: '組織', dataIndex: 'clientName', width: 180 },
 *   { ...hideBelow('lg'), title: '建立時間', dataIndex: 'createdAt', width: 160 },
 *   { ...hideBelow('xl'), title: '備註', dataIndex: 'remark' },
 * ];
 * ```
 *
 * 配合 Table 的 `scroll={{ x: 'max-content' }}` 可在小螢幕保留橫向滾動兜底。
 */
import type { Breakpoint } from 'antd';

/** antd Grid 斷點順序 */
const ORDER: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

/**
 * 返回一個 partial column object，包含 `responsive` 屬性，
 * 表示「只在 ≥ minBreakpoint 的螢幕上顯示」。
 *
 * @param minBreakpoint - 最小可見斷點，例如 'lg' 表示 < 992px 隱藏
 *
 * @example
 * { ...hideBelow('lg'), title: '建立時間', dataIndex: 'createdAt' }
 */
export function hideBelow(minBreakpoint: Breakpoint): { responsive: Breakpoint[] } {
  const startIdx = ORDER.indexOf(minBreakpoint);
  if (startIdx === -1) {
    // 未知斷點時 fallback 到全顯
    return { responsive: [...ORDER] };
  }
  return { responsive: ORDER.slice(startIdx) };
}

/**
 * 直觀別名：在 Pad 直立（< lg）以下隱藏。
 * 適用於"非關鍵列"如建立時間、備註、操作人姓名等。
 */
export function hideBelowLg(): { responsive: Breakpoint[] } {
  return hideBelow('lg');
}

/**
 * 直觀別名：在 Pad 橫向（< xl）以下隱藏。
 * 適用於"次要參考列"如審計快照欄、最後同步時間等。
 */
export function hideBelowXl(): { responsive: Breakpoint[] } {
  return hideBelow('xl');
}
